import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { useInitAuth } from '@diggens/user-management';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { Layout } from './components/layout/Layout';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { SetPassword } from './pages/auth/SetPassword';
import { Home } from './pages/Home';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './providers/ThemeProvider';
import { NodeListView, NodeDetailView } from './components/graph';
import { PipelineView } from './components/pipeline';
import { Dashboard, DashboardDetail } from './components/dashboard';
import { Analytics } from './pages/Analytics';
import { Settings } from './pages/Settings';
import { Data } from './pages/Data';
import { VehicleScenarioPage } from './pages/VehicleScenarioPage';
import { Lists } from './pages/Lists';
import { ListDetail } from './pages/ListDetail';
import { SchemaProvider, useSchema } from './contexts';

/**
 * Every route: the built-in literal routes, plus one list (or board) route and one detail route per nav item of each
 * app installed in the tenant. The literal routes stay for this spike; the schema's nav supplies the rest.
 */
function AppRoutes() {
  const { nav, views } = useSchema();

  const appRoutes = nav.flatMap((category) =>
    category.items.flatMap((item) => {
      const view = views.find((candidate) => candidate.key === item.viewKey && candidate.appKey === category.appKey);
      const page =
        view?.kind === 'kanban' ? (
          <PipelineView
            nodeType={item.objectType}
            picklistName={view.picklistName}
            stageField={view.stageField}
            title={item.label}
            cardRelation={view.cardRelation}
            sumField={view.sumField}
            fixedFilters={view.filters}
            boardFilters={view.boardFilters}
            defaultSort={view.defaultSort}
          />
        ) : (
          <NodeListView type={item.objectType} title={item.label} columns={view?.columns} viewKey={view?.key} fixedFilters={view?.filters} defaultSort={view?.defaultSort} />
        );
      const routes = [
        <Route
          key={item.path}
          path={item.path}
          element={
            <ProtectedRoute>
              <Layout>{page}</Layout>
            </ProtectedRoute>
          }
        />,
      ];
      if (view?.kind !== 'kanban') {
        routes.push(
          <Route
            key={`${item.path}/:id`}
            path={`${item.path}/:id`}
            element={
              <ProtectedRoute>
                <Layout>
                  <NodeDetailView type={item.objectType} />
                </Layout>
              </ProtectedRoute>
            }
          />
        );
      }
      return routes;
    })
  );

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/auth/set-password" element={<SetPassword />} />

      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <Layout>
              <Home />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Generic Graph Routes */}
      <Route
        path="/people"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="Person" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/people/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="Person" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizations"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="Organization" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizations/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="Organization" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/opportunities"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="Opportunity" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/opportunities/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="Opportunity" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/deals"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="Deal" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/deals/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="Deal" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activities"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="Activity" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activities/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="Activity" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="Document" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="Document" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="Task" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="Task" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="Campaign" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="Campaign" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="Event" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="Event" />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Campaign Lists */}
      <Route
        path="/lists"
        element={
          <ProtectedRoute>
            <Layout>
              <Lists />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lists/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ListDetail />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Portfolio */}
      <Route
        path="/portfolio-companies"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="Organization" filter={{ type: 'portfolio_company' }} title="Portfolio Companies" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/portfolio-companies/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="Organization" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/investments"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="Investment" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/investments/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="Investment" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/external-funds"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="ExternalFund" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/external-funds/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="ExternalFund" />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Fundraising */}
      <Route
        path="/vehicles"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="Vehicle" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vehicles/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="Vehicle" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vehicles/:id/scenarios"
        element={
          <ProtectedRoute>
            <Layout>
              <VehicleScenarioPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fundraising"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="FundraisingOpportunity" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fundraising/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="FundraisingOpportunity" />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* IR */}
      <Route
        path="/commitments"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="Commitment" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/commitments/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="Commitment" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/investment-accounts"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="InvestmentAccount" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/investment-accounts/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="InvestmentAccount" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/capital-calls"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="CapitalCall" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/capital-calls/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="CapitalCall" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/distributions"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="Distribution" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/distributions/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="Distribution" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeListView type="Report" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NodeDetailView type="Report" />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Pipeline Views */}
      <Route
        path="/opportunities/pipeline"
        element={
          <ProtectedRoute>
            <Layout>
              <PipelineView moduleId="sales" nodeType="Opportunity" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/deals/pipeline"
        element={
          <ProtectedRoute>
            <Layout>
              <PipelineView moduleId="pevc-dealflow" nodeType="Deal" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fundraising/pipeline"
        element={
          <ProtectedRoute>
            <Layout>
              <PipelineView moduleId="pevc-fundraising" nodeType="FundraisingOpportunity" />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Dashboards */}
      <Route
        path="/dealflow/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard moduleId="pevc-dealflow" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/portfolio/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard moduleId="pevc-portfolio" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fundraising/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard moduleId="pevc-fundraising" />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ir/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard moduleId="pevc-ir" />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Analytics */}
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <Layout>
              <Analytics />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics/dashboards/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <DashboardDetail />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Data */}
      <Route
        path="/data"
        element={
          <ProtectedRoute>
            <Layout>
              <Data />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Settings; the optional segment picks the tab (profile, users, apps, graph) */}
      <Route
        path="/settings/:section?"
        element={
          <ProtectedRoute>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Installed apps: list, detail and board routes from the tenant schema */}
      {appRoutes}

      <Route path="/" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

export default function App() {
  useInitAuth();

  return (
    <ThemeProvider>
      <CssBaseline />
      <ToastProvider>
        <BrowserRouter>
          <SchemaProvider>
            <AppRoutes />
          </SchemaProvider>
        </BrowserRouter>
      </ToastProvider>
    </ThemeProvider>
  );
}
